import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../utils/toast";
import {
  AlertTriangle,
  FolderPlus,
  ListFilter,
  MapPin,
  Plus,
  RotateCw,
  Settings2,
  Store,
  Trash2,
  X,
} from "lucide-react";

import { Drawer } from "../../../components/ui/Drawer";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Select } from "../../../components/ui/Select";
import { Switch } from "../../../components/ui/Switch";
import { SectionCard } from "../../../components/ui/SectionCard";
import { Skeleton } from "../../../components/ui/Skeleton";
import { FieldLabel } from "../../../components/ui/FieldLabel";
import { cn } from "../../../utils/cn";
import { getOrderRules, updateOrderRules } from "../../../api/integrations";
import { getRegions, getCommunesByRegion, getMyCoverage } from "../../../api/settings";
import { useCountry } from "../../../context/CountryContext";

/**
 * Drawer de "Reglas de creación de pedidos" de una integración (Jumpseller,
 * WooCommerce, PrestaShop, Shopify, TiendaNube y Wix — los valores/hints por
 * plataforma viven en PLATFORM_PROFILES).
 * Define qué pedidos crean paquete en Beartrack: cobertura región/comuna,
 * motor AND/OR de condiciones (pago, despacho/retiro, precio, producto, etc.),
 * mapeo de retiro y comportamiento ante datos faltantes. Persiste vía
 * PUT /integrations/:id/rules. Reemplaza al OrderRulesModal legacy 1:1 en
 * lógica; si el GET de reglas falla NO se muestra el formulario (editar sobre
 * defaults sobreescribiría las reglas reales del servidor sin que el usuario
 * lo sepa).
 */

// Metadata de campos (espejo del backend lib/order-rules/order-rules.fields.ts).
// Los labels son claves i18n (español natural); se traducen al renderizar.
const FIELDS = [
  // paymentStatus: enumValues/hint se resuelven POR PLATAFORMA (ver
  // PLATFORM_PROFILES); estos son el fallback (Jumpseller).
  { key: "paymentStatus", label: "Estado de pago", type: "string", enumValues: ["Paid", "Pending Payment", "Canceled", "Abandoned"] },
  { key: "shippingKind", label: "Tipo de envío", type: "string", enumValues: ["delivery", "pickup"] },
  { key: "shippingMethodName", label: "Nombre del método de envío", type: "string" },
  { key: "orderTotal", label: "Total del pedido", type: "number" },
  { key: "weightTotal", label: "Peso total (kg)", type: "number" },
  { key: "productSku", label: "SKU de producto", type: "stringSet" },
  { key: "productId", label: "ID de producto", type: "stringSet" },
  { key: "category", label: "Categoría (requiere datos de producto)", type: "stringSet" },
  { key: "maxDimension", label: "Tamaño máx. de un producto, cm (req. datos de producto)", type: "number" },
  { key: "volume", label: "Volumen total, cm³ (req. datos de producto)", type: "number" },
];

const OPS_BY_TYPE = {
  string: [
    { key: "eq", label: "es igual a" },
    { key: "neq", label: "es distinto de" },
    { key: "in", label: "es uno de" },
    { key: "nin", label: "no es ninguno de" },
    { key: "contains", label: "contiene" },
  ],
  number: [
    // Símbolos matemáticos: no se traducen.
    { key: "eq", label: "=" },
    { key: "neq", label: "≠" },
    { key: "gte", label: "≥" },
    { key: "lte", label: "≤" },
    { key: "between", label: "entre" },
  ],
  stringSet: [
    { key: "anyOf", label: "incluye alguno de" },
    { key: "allOf", label: "incluye todos" },
    { key: "noneOf", label: "no incluye ninguno de" },
  ],
};

// Los símbolos (=, ≠, ≥, ≤) pasan tal cual; el resto son claves i18n.
const opLabel = (t, label) => (/^[=≠≥≤]$/.test(label) ? label : t(label));

// ── Perfiles por plataforma ─────────────────────────────────────────────────
// El motor de reglas es genérico, pero los VALORES documentados que envía cada
// e-commerce difieren. Fuentes: API de Jumpseller (order.status); doc oficial
// de estados de WooCommerce (processing/completed = pagado → el plugin v2
// deriva payment_status con $order->is_paid()); PrestaShop deriva con
// Order::hasBeenPaid(). La comparación del motor es case/acento-insensible,
// así que reglas antiguas escritas distinto siguen matcheando.

// Las medidas de Shopify no vienen en el pedido: dependen de que el comercio
// las confirme producto a producto en la app. Sin este aviso, un pedido con un
// producto sin confirmar cae en `onDataUnavailable` y el comercio no tiene cómo
// saber por qué. Cita el label visible del selector "Si falta el dato": si ese
// label cambia, este texto tiene que acompañarlo.
const SHOPIFY_DIMENSIONS_HINT =
  "Solo para tiendas conectadas con la app oficial de Beartrack y con medidas confirmadas en ella; las conectadas por credenciales no envían medidas. Si faltan, se aplica «Si falta el dato».";

const PLATFORM_PROFILES = {
  Shopify: {
    paymentStatus: {
      // displayFinancialStatus de la Admin API, en MAYUSCULAS: es el valor que
      // emite buildShopifyOrderFacts. Escribirlo en minusculas hace que la
      // regla no matchee nunca y el comercio no tiene forma de darse cuenta.
      enumValues: [
        "PAID",
        "PENDING",
        "AUTHORIZED",
        "PARTIALLY_PAID",
        "PARTIALLY_REFUNDED",
        "REFUNDED",
        "VOIDED",
        "EXPIRED",
      ],
      hint: "Estado de pago del pedido segun la Admin API de Shopify.",
      defaultValue: "PAID",
    },
    // `maxDimension` y `volume` SI se ofrecen: la app de Shopify manda
    // `products[].dimensions` (cm) y `volume` (cm3) por unidad, leidos de los
    // metafields que el comercio confirmo, y shopify-facts.mapper.ts los
    // calcula con la misma semantica completo-o-nada de Woo. Un pedido con
    // algun producto sin las tres medidas deja la condicion en "desconocido" y
    // aplica `onDataUnavailable`, igual que en Woo con un producto sin medidas.
    //
    // Siguen fuera: `weightTotal` queda en `undefined` en el mapper (el peso ya
    // viaja en products[] pero no esta conectado); `product_id` la app solo lo
    // manda con `read_products` (`li.product?.id` en order.mapper.ts) y
    // habilitarlo como filtro no es parte de esta fase; y las categorias
    // exigirian un GET por producto que nadie hace.
    unavailableFields: ["weightTotal", "productId", "category"],
    fieldHints: {
      maxDimension: SHOPIFY_DIMENSIONS_HINT,
      volume: SHOPIFY_DIMENSIONS_HINT,
    },
    pickupSubtitle:
      "Shopify no distingue el retiro en tienda de forma nativa en el payload; declara aqui los nombres de tus metodos de retiro.",
  },
  TiendaNube: {
    paymentStatus: {
      // Los 8 valores de payment_status de la API 2025-03 de TiendaNube.
      enumValues: [
        "paid",
        "pending",
        "authorized",
        "partially_paid",
        "partially_refunded",
        "refunded",
        "voided",
        "abandoned",
      ],
      hint: "Estado de pago del pedido según la API de TiendaNube.",
      defaultValue: "paid",
    },
    // Las categorías exigirían un GET /products por producto; aún no se resuelven.
    unavailableFields: ["category"],
    pickupSubtitle:
      "TiendaNube marca el retiro de forma nativa; declara aquí solo métodos de retiro con nombres propios.",
  },
  Wix: {
    paymentStatus: {
      // Enum `paymentStatus` de la Orders API de Wix, en MAYUSCULAS y con
      // guion bajo: es literalmente lo que viaja en el pedido. Escribirlo en
      // minusculas o con el vocabulario de otra plataforma hace que la regla no
      // matchee NUNCA y el comercio no tiene forma de darse cuenta.
      enumValues: [
        "UNSPECIFIED",
        "NOT_PAID",
        "PAID",
        "PARTIALLY_REFUNDED",
        "FULLY_REFUNDED",
        "PENDING",
        "PENDING_MERCHANT",
      ],
      hint: "Estado de pago del pedido según la API de Wix.",
      defaultValue: "PAID",
    },
    // Verificado contra wix-facts.mapper.ts (buildOrderFacts): `categories`,
    // `dimensionsMax` y `volume` salen hardcodeados en `undefined` -el pedido
    // de Wix eCom no trae dimensiones en `physicalProperties` y las categorias
    // exigirian consultar el catalogo, que es Fase 2-. Los nombres de campo del
    // motor mapean via FIELD_META: category->categories, maxDimension->
    // dimensionsMax, volume->volume.
    //
    // NO se copia el perfil de TiendaNube aunque el mapper sea su espejo: ese
    // SI calcula dimensionsMax y volume. Marcar de menos es el error caro: un
    // hecho que nunca llega deja la condicion en "desconocido" y
    // `onDataUnavailable` RETIENE por defecto, o sea que frena TODOS los
    // pedidos del comercio en silencio.
    //
    // `weightTotal` y `productId` SI llegan (el mapper suma
    // `physicalProperties.weight` convirtiendo LB->KG, y saca los ids de
    // `catalogReference.catalogItemId`), asi que no van aca.
    unavailableFields: ["category", "maxDimension", "volume"],
    pickupSubtitle:
      "Wix marca el retiro en tienda de forma nativa; declara aquí solo métodos de retiro con nombres propios.",
  },
  Jumpseller: {
    paymentStatus: {
      enumValues: ["Paid", "Pending Payment", "Canceled", "Abandoned"],
      hint: "Estado de pago del pedido según la API de Jumpseller.",
      defaultValue: "Paid",
    },
    // Única plataforma con fase 2 de datos de producto: resuelve categorías,
    // dimensiones y volumen con un GET /products/:id antes de evaluar.
    unavailableFields: [],
    pickupSubtitle:
      "Jumpseller no marca el retiro; declara qué nombres de método de envío cuentan como retiro.",
  },
  WooCommerce: {
    paymentStatus: {
      enumValues: ["paid", "pending"],
      hint: "Derivado del pedido WooCommerce: \"paid\" = pago confirmado (estados processing/completed).",
      defaultValue: "paid",
    },
    unavailableFields: ["category"],
    pickupSubtitle:
      "El retiro nativo (local_pickup) se detecta solo; declara aquí métodos de retiro con otros nombres.",
  },
  Prestashop: {
    paymentStatus: {
      enumValues: ["paid", "pending"],
      hint: "Derivado del pedido PrestaShop: \"paid\" = pago aceptado por la tienda.",
      defaultValue: "paid",
    },
    unavailableFields: ["category"],
    pickupSubtitle:
      "Declara qué transportistas cuentan como retiro. Recuerda: el módulo solo envía pedidos cuyo carrier es BearTrack.",
  },
};

export const profileFor = (integrationName) =>
  PLATFORM_PROFILES[integrationName] ?? PLATFORM_PROFILES.Jumpseller;

// Contexto para que las filas anidadas (ConditionRow/GroupEditor) conozcan la
// plataforma sin prop-drilling a través del árbol recursivo.
const PlatformContext = createContext("Jumpseller");

export const fieldMeta = (key, profile = PLATFORM_PROFILES.Jumpseller) => {
  const base = FIELDS.find((f) => f.key === key) || FIELDS[0];
  if (base.key === "paymentStatus") {
    return {
      ...base,
      enumValues: profile.paymentStatus.enumValues,
      hint: profile.paymentStatus.hint,
    };
  }
  // La plataforma no puebla este hecho. La condición existente sigue VISIBLE a
  // propósito -si se ocultara, el comercio no podría corregir una regla que ya
  // guardó- pero se advierte en el label y no se puede elegir de nuevo.
  //
  // Por qué importa: un hecho que nunca llega hace que su condición valga
  // "desconocido"; si el resto del árbol no la desambigua se aplica
  // `onDataUnavailable`, que por defecto RETIENE. O sea que una regla sobre un
  // campo inexistente frena todos los pedidos, sin nada en la interfaz que lo
  // explique.
  if ((profile.unavailableFields ?? []).includes(base.key)) {
    return {
      ...base,
      unavailable: true,
      // Se reemplaza el parentesis existente en vez de apilar otro: varios
      // labels ya traen "(req. datos de producto)" y encadenarlos se lee mal.
      label: `${base.label.replace(/\s*\([^)]*\)\s*$/, "")} (no disponible en esta plataforma)`,
    };
  }
  // Aviso de la plataforma sobre un campo que SÍ se ofrece pero cuyo dato
  // depende de algo fuera del pedido. Viaja como `hint`: el mismo texto de
  // ayuda que ConditionRow ya muestra bajo la condición (el del estado de pago).
  const hint = profile.fieldHints?.[base.key];
  return hint ? { ...base, hint } : base;
};

const EMPTY_RULES = {
  enabled: false,
  mode: "allow",
  onDataUnavailable: "hold",
  coverage: { enabled: false, comunaIds: [], comunaNames: [], regionIds: [] },
  root: { combinator: "AND", children: [] },
  pickupMethodNames: [],
  treatZeroShippingAsPickup: false,
};

const titleCase = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const isGroup = (node) => node && typeof node === "object" && "combinator" in node;

const newCondition = (profile = PLATFORM_PROFILES.Jumpseller) => ({
  field: "paymentStatus",
  op: "eq",
  value: profile.paymentStatus.defaultValue,
});
const newGroup = () => ({ combinator: "AND", children: [] });

function defaultValueFor(meta, op) {
  if (op === "between") return [0, 0];
  if (["in", "nin", "anyOf", "allOf", "noneOf"].includes(op)) return [];
  if (meta.type === "number") return 0;
  if (meta.enumValues) return meta.enumValues[0];
  return "";
}

// ── Chip removible (comunas / métodos de retiro) ───────────────────────────
function RemovableChip({ label, onRemove, removeLabel }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-bg px-2.5 py-1 text-xs text-fg">
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        // after:-inset-2 expande el área táctil sin agrandar el ícono
        className="relative -mr-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-fg-muted transition-colors after:absolute after:-inset-2 after:content-[''] hover:bg-bg-subtle hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </span>
  );
}

// ── Editor del valor de una condición ──────────────────────────────────────
function ValueEditor({ meta, op, value, onChange }) {
  const { t } = useTranslation();

  // Arrays: in/nin/anyOf/allOf/noneOf -> texto separado por comas.
  if (["in", "nin", "anyOf", "allOf", "noneOf"].includes(op)) {
    const text = Array.isArray(value) ? value.join(", ") : "";
    return (
      <Input
        aria-label={t("Valores (separados por coma)")}
        placeholder={t("valores separados por coma")}
        value={text}
        onChange={(e) =>
          onChange(
            e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s.length > 0),
          )
        }
      />
    );
  }
  if (op === "between") {
    const [a, b] = Array.isArray(value) ? value : [0, 0];
    return (
      <div className="flex items-center gap-1.5">
        <Input
          type="number"
          aria-label={t("Desde")}
          value={a}
          onChange={(e) => onChange([Number(e.target.value), Number(b)])}
        />
        <span className="text-xs text-fg-muted">{t("y")}</span>
        <Input
          type="number"
          aria-label={t("Hasta")}
          value={b}
          onChange={(e) => onChange([Number(a), Number(e.target.value)])}
        />
      </div>
    );
  }
  if (meta.type === "number") {
    return (
      <Input
        type="number"
        aria-label={t("Valor")}
        value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    );
  }
  if (meta.enumValues) {
    return (
      <Select
        aria-label={t("Valor")}
        value={value ?? meta.enumValues[0]}
        onChange={(e) => onChange(e.target.value)}
        // Valores del backend (Paid, delivery, …): se muestran tal cual.
        options={meta.enumValues.map((v) => ({ value: v, label: v }))}
      />
    );
  }
  return (
    <Input
      aria-label={t("Valor")}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// ── Fila de una condición ──────────────────────────────────────────────────
function ConditionRow({ condition, onChange, onRemove }) {
  const { t } = useTranslation();
  const profile = profileFor(useContext(PlatformContext));
  const meta = fieldMeta(condition.field, profile);
  const ops = OPS_BY_TYPE[meta.type];

  const setField = (field) => {
    const m = fieldMeta(field, profile);
    const op = OPS_BY_TYPE[m.type][0].key;
    onChange({ field, op, value: defaultValueFor(m, op) });
  };
  const setOp = (op) => onChange({ ...condition, op, value: defaultValueFor(meta, op) });
  const setValue = (value) => onChange({ ...condition, value });

  return (
    <div className="grid grid-cols-1 items-center gap-2 rounded-lg border border-border bg-bg p-2.5 sm:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)_minmax(0,1.25fr)_2.5rem]">
      <Select
        aria-label={t("Campo")}
        value={condition.field}
        onChange={(e) => setField(e.target.value)}
        options={FIELDS.map((f) => ({
          value: f.key,
          label: t(fieldMeta(f.key, profile).label),
          // Deshabilitado, no oculto: si la regla guardada usa este campo, el
          // navegador igual muestra la opción seleccionada y se puede cambiar
          // a otra. Ocultarla dejaria el selector en blanco.
          disabled: !!fieldMeta(f.key, profile).unavailable,
        }))}
      />
      <Select
        aria-label={t("Operador")}
        value={condition.op}
        onChange={(e) => setOp(e.target.value)}
        options={ops.map((o) => ({ value: o.key, label: opLabel(t, o.label) }))}
      />
      <ValueEditor meta={meta} op={condition.op} value={condition.value} onChange={setValue} />
      <Button
        variant="ghost"
        size="icon"
        className="justify-self-end text-fg-muted hover:bg-status-error-soft hover:text-status-error"
        title={t("Quitar condición")}
        aria-label={t("Quitar condición")}
        onClick={onRemove}
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
      </Button>
      {meta.hint && (
        <p className="text-xs text-fg-muted sm:col-span-4">{t(meta.hint)}</p>
      )}
    </div>
  );
}

// ── Editor recursivo de un grupo (AND/OR) ──────────────────────────────────
function GroupEditor({ group, onChange, onRemove, depth = 0 }) {
  const { t } = useTranslation();
  const setCombinator = (combinator) => onChange({ ...group, combinator });
  const updateChild = (i, child) => {
    const children = group.children.slice();
    children[i] = child;
    onChange({ ...group, children });
  };
  const removeChild = (i) => {
    const children = group.children.slice();
    children.splice(i, 1);
    onChange({ ...group, children });
  };
  const profile = profileFor(useContext(PlatformContext));
  const addCondition = () =>
    onChange({ ...group, children: [...group.children, newCondition(profile)] });
  const addGroup = () => onChange({ ...group, children: [...group.children, newGroup()] });

  return (
    <div className={cn("space-y-2", depth > 0 && "ml-1 border-l-2 border-border pl-3")}>
      <div className="flex items-center gap-2">
        {/* Control segmentado Y/O */}
        <div
          className="inline-flex overflow-hidden rounded-lg border border-border"
          role="group"
          aria-label={t("Combinador del grupo")}
        >
          {["AND", "OR"].map((c) => (
            <button
              key={c}
              type="button"
              aria-pressed={group.combinator === c}
              className={cn(
                "h-9 px-3 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 disabled:cursor-not-allowed",
                group.combinator === c
                  ? "bg-brand-500 text-white"
                  : "bg-bg text-fg-muted hover:bg-bg-muted hover:text-fg",
              )}
              onClick={() => setCombinator(c)}
            >
              {c === "AND" ? t("Y (todas)") : t("O (alguna)")}
            </button>
          ))}
        </div>
        {onRemove && (
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto h-9 w-9 text-fg-muted hover:bg-status-error-soft hover:text-status-error"
            title={t("Quitar grupo")}
            aria-label={t("Quitar grupo")}
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      </div>

      {group.children.length === 0 && (
        <p className="text-xs text-fg-muted">{t("Sin condiciones. Agrega una abajo.")}</p>
      )}

      {group.children.map((child, i) =>
        isGroup(child) ? (
          <GroupEditor
            key={i}
            group={child}
            depth={depth + 1}
            onChange={(g) => updateChild(i, g)}
            onRemove={() => removeChild(i)}
          />
        ) : (
          <ConditionRow
            key={i}
            condition={child}
            onChange={(c) => updateChild(i, c)}
            onRemove={() => removeChild(i)}
          />
        ),
      )}

      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Plus className="h-3.5 w-3.5" aria-hidden="true" />}
          onClick={addCondition}
        >
          {t("Condición")}
        </Button>
        {depth < 4 && (
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<FolderPlus className="h-3.5 w-3.5" aria-hidden="true" />}
            onClick={addGroup}
          >
            {t("Grupo")}
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Selector de cobertura región/comuna ────────────────────────────────────
function CoverageSelector({ coverage, onChange }) {
  const { t } = useTranslation();
  // País del comercio: el catálogo debe ser el suyo para que la intersección
  // con `my-coverage` (ids de región del operador) tenga sentido.
  const { countryId, loading: countryLoading } = useCountry();
  const switchId = useId();
  const [regions, setRegions] = useState([]);
  const [regionId, setRegionId] = useState("");
  const [communes, setCommunes] = useState([]);
  const [loadingComunas, setLoadingComunas] = useState(false);
  // Cobertura del operador: { byRegion: Map(regionId -> {full, ids:Set}), operators }
  const [available, setAvailable] = useState(null);
  const [availLoading, setAvailLoading] = useState(true);

  const selectedIds = useMemo(() => new Set(coverage.comunaIds || []), [coverage.comunaIds]);

  // Cobertura disponible del operador del comerciante (solo estas comunas son elegibles).
  useEffect(() => {
    (async () => {
      try {
        const resp = await getMyCoverage();
        const data = resp?.data;
        const byRegion = new Map();
        (data?.coverage || []).forEach((c) => {
          byRegion.set(String(c.regionId), {
            full: !!c.full,
            ids: new Set((c.communeIds || []).map(String)),
          });
        });
        setAvailable({ byRegion, operators: data?.operators || [] });
      } catch (e) {
        console.error("getMyCoverage error:", e);
        setAvailable({ byRegion: new Map(), operators: [] });
      } finally {
        setAvailLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (countryLoading || !countryId) return;
    (async () => {
      try {
        const resp = await getRegions(countryId);
        const data = resp?.data;
        if (Array.isArray(data)) setRegions(data);
      } catch (e) {
        console.error("getRegions error:", e);
      }
    })();
  }, [countryId, countryLoading]);

  useEffect(() => {
    if (!regionId) {
      setCommunes([]);
      return;
    }
    setLoadingComunas(true);
    (async () => {
      try {
        const resp = await getCommunesByRegion(regionId);
        // comunas_by_region.json devuelve { region_id, communes: [{_id, name}] }
        const list = resp?.data?.communes;
        setCommunes(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("getCommunesByRegion error:", e);
        setCommunes([]);
      } finally {
        setLoadingComunas(false);
      }
    })();
  }, [regionId]);

  // Solo regiones que el operador cubre.
  const coveredRegions = useMemo(() => {
    if (!available) return [];
    return regions.filter((r) => available.byRegion.has(String(r._id)));
  }, [regions, available]);

  // Solo comunas disponibles de la región elegida (full => toda la región).
  const availableCommunes = useMemo(() => {
    if (!available || !regionId) return [];
    const entry = available.byRegion.get(String(regionId));
    if (!entry) return [];
    if (entry.full) return communes;
    return communes.filter((c) => entry.ids.has(String(c._id)));
  }, [communes, available, regionId]);

  const toggleComuna = (c) => {
    const ids = new Set(coverage.comunaIds || []);
    const names = new Map(
      (coverage.comunaIds || []).map((id, i) => [id, (coverage.comunaNames || [])[i]]),
    );
    if (ids.has(c._id)) {
      ids.delete(c._id);
      names.delete(c._id);
    } else {
      ids.add(c._id);
      names.set(c._id, c.name);
    }
    const comunaIds = Array.from(ids);
    onChange({ ...coverage, comunaIds, comunaNames: comunaIds.map((id) => names.get(id)) });
  };

  const addWholeRegion = () => {
    const ids = new Set(coverage.comunaIds || []);
    const names = new Map(
      (coverage.comunaIds || []).map((id, i) => [id, (coverage.comunaNames || [])[i]]),
    );
    availableCommunes.forEach((c) => {
      ids.add(c._id);
      names.set(c._id, c.name);
    });
    const comunaIds = Array.from(ids);
    const regionIds = Array.from(new Set([...(coverage.regionIds || []), regionId])).filter(Boolean);
    onChange({ ...coverage, comunaIds, comunaNames: comunaIds.map((id) => names.get(id)), regionIds });
  };

  const removeComuna = (id) => {
    const idx = (coverage.comunaIds || []).indexOf(id);
    if (idx < 0) return;
    const comunaIds = (coverage.comunaIds || []).filter((x) => x !== id);
    const comunaNames = (coverage.comunaNames || []).filter((_, i) => i !== idx);
    onChange({ ...coverage, comunaIds, comunaNames });
  };

  return (
    <div className="space-y-3">
      <Switch
        id={switchId}
        checked={!!coverage.enabled}
        onChange={(enabled) => onChange({ ...coverage, enabled })}
        label={t("Filtrar por región / comuna de destino")}
      />

      {coverage.enabled && (
        <>
          <p className="text-xs text-fg-muted">
            {t(
              "Solo se crearán pedidos cuya comuna esté seleccionada. La lista se limita a las comunas que cubre tu operador logístico.",
            )}
          </p>

          {availLoading && (
            <div className="space-y-2" role="status">
              <span className="sr-only">{t("Cargando cobertura…")}</span>
              <Skeleton className="h-10 w-full max-w-xs" aria-hidden="true" />
              <Skeleton className="h-4 w-52" aria-hidden="true" />
            </div>
          )}

          {!availLoading && available && available.byRegion.size === 0 && (
            <p className="flex items-center gap-1.5 text-xs text-status-warning">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t(
                "No pudimos determinar la cobertura de tu operador. Contacta a soporte para habilitar el filtro por comuna.",
              )}
            </p>
          )}

          {!availLoading && available && available.byRegion.size > 0 && (
            <>
              <div className="flex flex-wrap items-end gap-2">
                <div className="w-full max-w-xs">
                  <Select
                    label={t("Región")}
                    placeholder={t("Selecciona una región…")}
                    value={regionId}
                    onChange={(e) => setRegionId(e.target.value)}
                    options={coveredRegions.map((r) => ({ value: r._id, label: r.name }))}
                  />
                </div>
                {regionId && availableCommunes.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mb-1"
                    iconLeft={<Plus className="h-3.5 w-3.5" aria-hidden="true" />}
                    onClick={addWholeRegion}
                  >
                    {t("Toda la región")}
                  </Button>
                )}
              </div>

              {loadingComunas && (
                <div className="space-y-1.5" role="status">
                  <span className="sr-only">{t("Cargando comunas…")}</span>
                  <Skeleton className="h-4 w-40" aria-hidden="true" />
                  <Skeleton className="h-4 w-32" aria-hidden="true" />
                </div>
              )}
              {!loadingComunas && regionId && availableCommunes.length === 0 && (
                <p className="text-xs text-fg-muted">
                  {t("Tu operador no cubre comunas en esta región.")}
                </p>
              )}
              {!loadingComunas && regionId && availableCommunes.length > 0 && (
                <div className="grid max-h-44 grid-cols-1 gap-x-4 gap-y-0.5 overflow-y-auto rounded-lg border border-border bg-bg p-3 sm:grid-cols-2">
                  {availableCommunes.map((c) => (
                    <label
                      key={c._id}
                      className="flex cursor-pointer items-center gap-2 rounded py-1 text-sm text-fg"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 shrink-0 accent-brand-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                        checked={selectedIds.has(c._id)}
                        onChange={() => toggleComuna(c)}
                      />
                      {c.name}
                    </label>
                  ))}
                </div>
              )}
            </>
          )}

          <div className="flex flex-wrap items-center gap-1.5">
            {(coverage.comunaIds || []).map((id, i) => (
              <RemovableChip
                key={id}
                label={titleCase((coverage.comunaNames || [])[i])}
                removeLabel={t("Quitar {{name}}", {
                  name: titleCase((coverage.comunaNames || [])[i]),
                })}
                onRemove={() => removeComuna(id)}
              />
            ))}
            {(coverage.comunaIds || []).length === 0 && (
              <span className="text-xs text-fg-muted">{t("Ninguna comuna seleccionada.")}</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Mapeo de retiro en tienda ──────────────────────────────────────────────
function PickupConfig({ names, treatZero, onNames, onTreatZero }) {
  const { t } = useTranslation();
  const inputId = useId();
  const switchId = useId();
  const [draft, setDraft] = useState("");

  const add = () => {
    const v = draft.trim();
    if (v && !names.includes(v)) onNames([...names, v]);
    setDraft("");
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <FieldLabel htmlFor={inputId}>{t("Nombre del método de envío")}</FieldLabel>
        <div className="flex gap-2">
          <Input
            id={inputId}
            className="max-w-xs"
            value={draft}
            placeholder={t('Ej: "Retiro en tienda"')}
            autoComplete="off"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          />
          <Button
            variant="outline"
            iconLeft={<Plus className="h-4 w-4" aria-hidden="true" />}
            onClick={add}
          >
            {t("Agregar")}
          </Button>
        </div>
      </div>

      {names.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {names.map((n) => (
            <RemovableChip
              key={n}
              label={n}
              removeLabel={t("Quitar {{name}}", { name: n })}
              onRemove={() => onNames(names.filter((x) => x !== n))}
            />
          ))}
        </div>
      )}

      <Switch
        id={switchId}
        checked={treatZero}
        onChange={onTreatZero}
        label={t("Tratar envío con costo 0 como retiro")}
      />
    </div>
  );
}

// ── Skeleton con la forma real del formulario (evita layout-jump) ──────────
function RulesSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="space-y-4" role="status" aria-live="polite">
      <span className="sr-only">{t("Cargando reglas…")}</span>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="space-y-4 rounded-lg border border-border bg-bg-muted p-5"
          aria-hidden="true"
        >
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="space-y-1.5">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-3 w-56 max-w-full" />
            </div>
          </div>
          <Skeleton className="h-6 w-44" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Drawer principal ───────────────────────────────────────────────────────
export default function OrderRulesDrawer({
  open,
  onClose,
  onSaved,
  integrationId,
  integrationName,
  alias,
}) {
  const { t } = useTranslation();
  const enabledId = useId();

  const [rules, setRules] = useState(EMPTY_RULES);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!integrationId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(false);
    const res = await getOrderRules(integrationId);
    if (res && res.ok) {
      const r = res.rules;
      if (r) {
        setRules({
          ...EMPTY_RULES,
          ...r,
          coverage: {
            enabled: !!r.coverage?.enabled,
            comunaIds: r.coverage?.comunaIds || [],
            // el GET devuelve nombres normalizados; los usamos como display (title-case en chips)
            comunaNames: r.coverage?.comunaNames || r.coverage?.comunaNamesNormalized || [],
            regionIds: r.coverage?.regionIds || [],
          },
          root: r.root || newGroup(),
          pickupMethodNames: r.pickupMethodNames || [],
          treatZeroShippingAsPickup: !!r.treatZeroShippingAsPickup,
        });
      } else {
        // Integración sin reglas todavía: defaults editables.
        setRules(EMPTY_RULES);
      }
    } else {
      // GET fallido: sin formulario. Editar defaults aquí sobreescribiría las
      // reglas reales al guardar sin que el usuario lo sepa.
      setLoadError(true);
    }
    setLoading(false);
  }, [integrationId]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (p) => setRules((prev) => ({ ...prev, ...p }));

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      enabled: rules.enabled,
      mode: rules.mode,
      onDataUnavailable: rules.onDataUnavailable,
      coverage: {
        enabled: rules.coverage.enabled,
        comunaIds: rules.coverage.comunaIds,
        comunaNames: rules.coverage.comunaNames,
        regionIds: rules.coverage.regionIds,
      },
      root: rules.root,
      pickupMethodNames: rules.pickupMethodNames,
      treatZeroShippingAsPickup: rules.treatZeroShippingAsPickup,
    };
    try {
      const res = await updateOrderRules(integrationId, payload);
      if (res?.error) {
        const errs = res.error?.errors;
        // Mensajes de validación del backend tal cual (contrato); el drawer queda abierto.
        toast.error(Array.isArray(errs) ? errs.join(" · ") : t("No se pudieron guardar las reglas"));
        return;
      }
      toast.success(t("Reglas guardadas"));
      if (typeof onSaved === "function") onSaved();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error(t("Error al guardar las reglas"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="lg"
      title={t("Reglas de creación de pedidos")}
      description={
        <>
          {t("Define qué pedidos de")}{" "}
          <strong className="font-medium text-fg">
            {integrationName}
            {alias ? ` · ${alias}` : ""}
          </strong>{" "}
          {t("se crean en Beartrack.")}
        </>
      }
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t("Cancelar")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={saving}
            disabled={saving || loading || loadError}
          >
            {saving ? t("Guardando…") : t("Guardar reglas")}
          </Button>
        </>
      }
    >
      {loading ? (
        <RulesSkeleton />
      ) : loadError ? (
        <div
          className="flex flex-col items-center gap-3 rounded-lg border border-border bg-bg-muted px-6 py-10 text-center"
          role="alert"
        >
          <span
            className="flex h-11 w-11 items-center justify-center rounded-full bg-status-error-soft text-status-error"
            aria-hidden="true"
          >
            <AlertTriangle className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-fg">
              {t("No pudimos cargar las reglas guardadas")}
            </p>
            <p className="mt-1 max-w-sm text-sm text-fg-muted">
              {t(
                "Para evitar sobrescribir tu configuración, el editor está deshabilitado hasta poder leer las reglas actuales.",
              )}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconLeft={<RotateCw className="h-4 w-4" aria-hidden="true" />}
            onClick={load}
          >
            {t("Reintentar")}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <SectionCard
            icon={<Settings2 className="h-4 w-4" />}
            title={t("General")}
            subtitle={t("Activa el motor de reglas y define su comportamiento base.")}
          >
            <div className="space-y-4">
              <Switch
                id={enabledId}
                checked={rules.enabled}
                onChange={(enabled) => patch({ enabled })}
                label={t("Activar reglas")}
              />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Select
                  label={t("Modo")}
                  value={rules.mode}
                  onChange={(e) => patch({ mode: e.target.value })}
                  options={[
                    { value: "allow", label: t("Crear solo si cumple") },
                    { value: "block", label: t("No crear si cumple") },
                  ]}
                />
                <Select
                  label={t("Si falta el dato")}
                  value={rules.onDataUnavailable}
                  onChange={(e) => patch({ onDataUnavailable: e.target.value })}
                  options={[
                    { value: "hold", label: t("Retener (revisar)") },
                    { value: "skip", label: t("No crear") },
                    { value: "create", label: t("Crear igual") },
                  ]}
                />
              </div>
            </div>
          </SectionCard>

          {/* Igual que el legacy: con las reglas apagadas el resto se deshabilita
              pero sigue visible (el usuario ve qué configuraría al activarlas). */}
          <fieldset
            disabled={!rules.enabled}
            className={cn("m-0 min-w-0 space-y-4 border-0 p-0", !rules.enabled && "opacity-50")}
          >
            <SectionCard
              icon={<MapPin className="h-4 w-4" />}
              title={t("Cobertura")}
              subtitle={t("Limita la creación de pedidos a las comunas de destino que cubre tu operador.")}
            >
              <CoverageSelector
                coverage={rules.coverage}
                onChange={(coverage) => patch({ coverage })}
              />
            </SectionCard>

            <SectionCard
              icon={<ListFilter className="h-4 w-4" />}
              title={t("Condiciones")}
              subtitle={t("Combina condiciones con Y/O para decidir qué pedidos aplican.")}
            >
              <PlatformContext.Provider value={integrationName}>
                <GroupEditor group={rules.root} onChange={(root) => patch({ root })} />
              </PlatformContext.Provider>
            </SectionCard>

            <SectionCard
              icon={<Store className="h-4 w-4" />}
              title={t("Retiro en tienda")}
              subtitle={t(profileFor(integrationName).pickupSubtitle)}
            >
              <PickupConfig
                names={rules.pickupMethodNames}
                treatZero={rules.treatZeroShippingAsPickup}
                onNames={(pickupMethodNames) => patch({ pickupMethodNames })}
                onTreatZero={(treatZeroShippingAsPickup) => patch({ treatZeroShippingAsPickup })}
              />
            </SectionCard>
          </fieldset>
        </div>
      )}
    </Drawer>
  );
}
